import { getPromoTags } from 'promo-tag-utils';
import {
  normalizeTags,
  getSaleSettings,
  hasSaleTag
} from 'rebuy-product-utils';
import { html, safeUrl, safeImageUrl, cssToken } from 'html-utils';

window.BSS = window.BSS || {};
BSS.Components = BSS.Components || {};

/**
 * Defines the `BSS.Components.ProductRecommendations` module which fetches product recommendations
 * from Rebuy and renders them using the product-card web component
 * @module BSS.Components.ProductRecommendations
 * @returns {Object} An object exposing public methods for product recommendations
 */
BSS.Components.ProductRecommendations = (function () {
  // Rebuy API configuration
  const REBUY_API_KEY = '92d7bbc7a53a1ca6ad2ba36f5d00a3efecd8ec16';
  const REBUY_RULESET_ID = '152133';
  const REBUY_BASE_URL = `https://rebuyengine.com/api/v1/custom/id/${REBUY_RULESET_ID}?key=${REBUY_API_KEY}`;
  const REBUY_WIDGET_ID = '151645';

  /**
   * Generates HTML for color swatches
   * @param {Object} product - Product data
   * @returns {string} HTML string for swatches
   */
  function generateSwatchesHtml(product) {
    if (!product.options || !product.variants) return '';

    // Find color option index
    const colorOptionIndex = product.options.findIndex(
      opt => opt.toLowerCase() === 'color'
    );
    if (colorOptionIndex < 0) return '';

    // Get unique colors and their first variant IDs
    const colorOption = `option${colorOptionIndex + 1}`;
    const seenColors = new Set();
    const colorVariants = [];

    product.variants.forEach(variant => {
      const color = variant[colorOption];
      if (
        color &&
        !seenColors.has(color) &&
        !color.toLowerCase().includes('discontinued')
      ) {
        seenColors.add(color);
        colorVariants.push({ color, variantId: variant.id });
      }
    });

    if (colorVariants.length === 0) return '';

    // product.url already has query params, so variant is appended with &
    const productUrl = safeUrl(product.url, { fallback: '#' });
    const swatches = colorVariants.map(
      ({ color, variantId }) => html`
        <a
          href="${safeUrl(
            `${product.url}&variant=${encodeURIComponent(variantId)}`,
            { fallback: '#' }
          )}"
          title="${color}"
          aria-label="View ${product.title} in ${color}"
          class="swatch ${cssToken(color)}"
          ><span class="swatch-color-square"></span
        ></a>
      `
    );

    const moreColors =
      colorVariants.length > 4
        ? html`<a
            href="${productUrl}"
            aria-label="Shop ${product.title}"
            class="more-colors"
          >
            <span class="more-colors-caret">></span>
          </a>`
        : '';

    return html`${swatches}${moreColors}`;
  }

  /**
   * Maps Rebuy product data to the format expected by the product-card component
   * @param {Object} rebuyProduct - Product data from Rebuy API
   * @returns {Object} Mapped product data
   */
  function mapRebuyProductToProduct(rebuyProduct) {
    if (!rebuyProduct) return null;

    // Ensure we have required data
    if (!rebuyProduct.variants || rebuyProduct.variants.length === 0) {
      console.warn('Product missing variants:', rebuyProduct);
      return null;
    }

    const product = {
      title: rebuyProduct?.title,
      tags: normalizeTags(rebuyProduct),
      price: parseFloat(rebuyProduct?.variants[0]?.price || 0) * 100,
      compare_at_price: rebuyProduct?.variants[0]?.compare_at_price
        ? parseFloat(rebuyProduct.variants[0].compare_at_price) * 100
        : null,
      images: (rebuyProduct?.images || []).map(img => img.src),
      featured_image:
        rebuyProduct?.image?.src || rebuyProduct?.images?.[0]?.src,
      // Map options to match Shopify format (array of strings like ["Color", "Size"])
      options: (rebuyProduct?.options || []).map(opt => opt.name),
      variants: rebuyProduct?.variants.map(variant => ({
        id: variant.id,
        option1: variant.option1,
        option2: variant.option2,
        option3: variant.option3
      })),
      url: `/products/${encodeURIComponent(rebuyProduct?.handle ?? '')}?_rdiscovery-handle=${encodeURIComponent(rebuyProduct?.handle ?? '')}&_rdiscovery-widget=${REBUY_WIDGET_ID}`,
      selected_or_first_available_variant: {
        url: `/products/${encodeURIComponent(rebuyProduct?.handle ?? '')}?variant=${encodeURIComponent(rebuyProduct?.variants[0]?.id ?? '')}&_rdiscovery-handle=${encodeURIComponent(rebuyProduct?.handle ?? '')}&_rdiscovery-widget=${REBUY_WIDGET_ID}`
      }
    };

    return product;
  }

  /**
   * Applies sale pricing logic to product data
   * @param {Object} product - Mapped product data
   * @returns {Object} Product data with sale pricing applied
   */
  function applySaleLogic(product) {
    // Get sale settings from global settings
    const saleSettings = getSaleSettings();

    if (!saleSettings.isEnabled) {
      return product;
    }

    if (hasSaleTag(product, saleSettings)) {
      const discountPercentage = saleSettings.discountPercentage / 100;
      const discountMultiplier = 1 - discountPercentage;

      product.sale_price = Math.round(product.price * discountMultiplier);

      if (!product.compare_at_price) {
        product.compare_at_price = product.price;
      }
    }

    return product;
  }

  /**
   * Fetches product recommendations from Rebuy
   * @param {string} productId - The Shopify product ID
   * @returns {Promise<Array>} Array of product recommendations
   */
  async function fetchRecommendations(productId) {
    try {
      const url = `${REBUY_BASE_URL}&shopify_product_ids=${productId}&format=pretty`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Rebuy API error: ${response.statusText}`);
      }

      const response_data = await response.json();

      // Rebuy API returns products in the 'data' property
      const products = response_data.data || [];

      if (!Array.isArray(products)) {
        return [];
      }

      if (products.length === 0) {
        console.log('No product recommendations returned');
        return [];
      }

      // Limit to first 4 products
      const limitedProducts = products.slice(0, 4);

      // Map and apply sale logic to products
      const mappedProducts = limitedProducts
        .map(rebuyProduct => {
          const product = mapRebuyProductToProduct(rebuyProduct);
          return product ? applySaleLogic(product) : null;
        })
        .filter(product => product !== null);

      return mappedProducts;
    } catch (error) {
      console.error('Error fetching Rebuy recommendations:', error);
      return [];
    }
  }

  /**
   * Renders product recommendations into a container
   * @param {HTMLElement} container - Container element to render products into
   * @param {Array} products - Array of product data
   */
  function renderProducts(container, products) {
    if (!container || !products.length) return;

    // Clear existing content
    container.innerHTML = '';

    products.forEach((product, index) => {
      // Create product card element
      const productCard = document.createElement('product-card');
      productCard.setAttribute('disable-image-carousel', '');

      // Get sale settings for rendering
      const saleSettings = getSaleSettings();
      const isSaleEligible = hasSaleTag(product, saleSettings);

      const money = (cents, digits = 2) =>
        `$${(Number(cents) / 100).toFixed(digits)}`;
      const price =
        isSaleEligible && saleSettings.isEnabled && product.sale_price
          ? html`
              <span class="product-card__amount product-card__amount--compare">
                ${money(product.compare_at_price, 0)}
              </span>
              <span class="product-card__amount red"
                >${money(product.sale_price)}</span
              >
            `
          : html`
              ${product.compare_at_price
                ? html`<span
                    class="product-card__amount product-card__amount--compare"
                  >
                    ${money(product.compare_at_price)}
                  </span>`
                : ''}
              <span class="product-card__amount">
                ${money(product.price)}
              </span>
            `;

      productCard.innerHTML = html`
        <div class="product-card">
          <div class="product-card__tag"></div>
          <a
            href="${safeUrl(product.selected_or_first_available_variant?.url, {
              fallback: '#'
            })}"
            class="product-card__image"
            tabindex="-1"
          >
            <img
              src="${safeImageUrl(product.featured_image)}"
              alt="${product.title}"
              loading="lazy"
            />
          </a>
          <a
            href="${safeUrl(product.url, { fallback: '#' })}"
            class="product-card__details"
            aria-label="Shop ${product.title}"
          >
            <h3 class="product-card__title">${product.title}</h3>
            <p class="product-card__price">${price}</p>
          </a>
          <div class="product-card__colors">
            ${generateSwatchesHtml(product)}
          </div>
        </div>
      `;

      const minimalProduct = {
        title: product.title,
        tags: product.tags,
        images: product.images
      };

      productCard.setAttribute('product', JSON.stringify(minimalProduct));

      container.appendChild(productCard);
    });

    // Render promo tags after all product cards are added to the DOM
    getPromoTags();
  }

  /**
   * Initializes product recommendations for a given section
   * @param {string} sectionId - The section ID to initialize
   * @param {Object} params - Parameters for the Rebuy API
   */
  async function init(productId) {
    const container = document.querySelector('.js-product-recommendations');
    if (!container) {
      console.warn(
        'Product recommendations container (.js-product-recommendations) not found in section'
      );
      return;
    }

    // Show loading state
    container.classList.add('loading');

    try {
      const products = await fetchRecommendations(productId);
      renderProducts(container, products);
    } catch (error) {
      console.error('Error initializing product recommendations:', error);
    } finally {
      container.classList.remove('loading');
    }
  }

  return {
    init,
    fetchRecommendations,
    renderProducts,
    mapRebuyProductToProduct,
    applySaleLogic
  };
})();
